/**
 * Utility functions for calculating token costs and tracking user spending
 */

// Model pricing constants (in USD per 1000 tokens)
const MODEL_PRICING = {
  // OpenAI models
  'gpt-4.1-mini': {
    input: 0.0004,   // $0.40 per million input tokens
    output: 0.0016   // $1.60 per million output tokens
  },
  // Claude models
  'anthropic.claude-3-5-haiku-20241022-v1:0': {
    input: 0.0008,   // $0.80 per million input tokens
    output: 0.004    // $4.00 per million output tokens
  },
  'arn:aws:bedrock:us-west-2:477591778252:inference-profile/us.meta.llama4-maverick-17b-instruct-v1:0': {
    input: 0.00024,  // $0.24 per million input tokens
    output: 0.00097  // $0.97 per million output tokens
  },
  // Llama models 
  'us.meta.llama3-3-70b-instruct-v1:0': {
    input: 0.00072,  // $0.72 per million input tokens
    output: 0.00072  // $0.72 per million output tokens
  },
  'arn:aws:bedrock:us-west-2:477591778252:inference-profile/us.meta.llama3-3-70b-instruct-v1:0': {
    input: 0.00072,  // $0.72 per million input tokens
    output: 0.00072  // $0.72 per million output tokens
  },
  // OpenAI TTS pricing
  'gpt-4o-mini-tts': {
    input: 0.0006,   // $0.60 per million input tokens (text)
    audio: 0.012     // $12.00 per million audio tokens (output)
  },
  // Default fallback pricing if model not found
  'default': {
    input: 0.003,    // $3.00 per million input tokens (conservative estimate)
    output: 0.015    // $15.00 per million output tokens (conservative estimate)
  }
};

/**
 * Calculate cost of token usage for a specific model
 * @param {Object} tokenUsage - Object containing inputTokens, outputTokens, totalTokens
 * @param {string} modelId - The model ID used for the request
 * @returns {Object} Cost breakdown in USD
 */
export const calculateTokenCost = (tokenUsage, modelId = 'default') => {
  // Use the specified model pricing or fallback to default
  const pricing = MODEL_PRICING[modelId] || 
    // Try to find pricing based on partial model ID match
    (Object.keys(MODEL_PRICING).find(key => 
      modelId.includes(key) && MODEL_PRICING[key]
    ) && MODEL_PRICING[Object.keys(MODEL_PRICING).find(key => 
      modelId.includes(key)
    )]) ||
    MODEL_PRICING['default'];
  
  // Extract token counts, defaulting to 0 if not provided
  const inputTokens = tokenUsage?.inputTokens || 0;
  const outputTokens = tokenUsage?.outputTokens || 0;
  
  // Calculate costs (converting from "per 1000 tokens" to "per token")
  const inputCost = (inputTokens * pricing.input) / 1000;
  const outputCost = (outputTokens * pricing.output) / 1000;
  const totalCost = inputCost + outputCost;
  
  return {
    inputCost,
    outputCost,
    totalCost,
    currency: 'USD',
    inputTokens,
    outputTokens,
    totalTokens: tokenUsage?.totalTokens || (inputTokens + outputTokens),
    modelId
  };
};

/**
 * Estimate tokens from text for TTS
 * Rule of thumb: ~1 token per 4 characters for English text
 * @param {string} text - The input text
 * @returns {number} Estimated token count
 */
export const estimateTokensFromText = (text) => {
  if (!text) return 0;
  // Remove extra whitespace and estimate tokens
  const cleanText = text.trim().replace(/\s+/g, ' ');
  return Math.ceil(cleanText.length / 4);
};

/**
 * Calculate TTS cost based on text input
 * @param {string} text - The input text
 * @param {string} modelId - TTS model ID (gpt-4o-mini-tts or tts-1-hd)
 * @returns {Object} Cost breakdown
 */
export const calculateTTSCost = (text, modelId = 'gpt-4o-mini-tts') => {
  const pricing = MODEL_PRICING[modelId] || MODEL_PRICING['gpt-4o-mini-tts'];
  
  const inputTokens = estimateTokensFromText(text);
  // For OpenAI TTS, audio tokens are approximately 1.25x character count
  const audioTokens = Math.ceil(text.length * 1.25);
  
  const inputCost = (inputTokens * pricing.input) / 1000;
  const audioCost = (audioTokens * pricing.audio) / 1000;
  const totalCost = inputCost + audioCost;
  
  return {
    inputCost,
    audioCost,
    totalCost,
    currency: 'USD',
    inputTokens,
    audioTokens,
    totalTokens: inputTokens + audioTokens,
    modelId,
    textLength: text.length
  };
};

/**
 * Get the week number for a given date
 * @param {Date} date - The date object
 * @returns {number} Week number
 */
const getWeekNumber = (date) => {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const daysSinceStartOfYear = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
  return Math.ceil((daysSinceStartOfYear + startOfYear.getDay() + 1) / 7);
};

/**
 * Update user spending in DynamoDB
 * @param {Object} tokenUsage - Object containing inputTokens, outputTokens, totalTokens
 * @param {string} userId - The user ID
 * @param {string} modelId - The model ID used for the request
 * @param {Object} dynamoUtils - DynamoDB utility instance
 * @param {string} categoryName - Category name for the spending (e.g., "Tutor Chat", "Image Description")
 * @returns {Promise<boolean>} - Whether the update was successful
 */
export const updateUserSpending = async (tokenUsage, userId, modelId, dynamoUtils, categoryName) => {
  if (!userId || !dynamoUtils) {
    console.error('Cannot update user spending - missing userId or dynamoUtils');
    return false;
  }
  
  if (!categoryName) {
    console.error('Cannot update user spending - missing categoryName');
    return false;
  }
  
  try {
    // 1. Calculate cost of this request
    const costInfo = calculateTokenCost(tokenUsage, modelId);
    console.log('Token usage cost calculation:', categoryName, costInfo);
    
    // 2. Determine service type based on model ID
    let serviceType = 'bedrock'; // Default
    if (modelId && modelId.startsWith('gpt-')) {
      serviceType = 'openai';
    } else if (modelId && (modelId.startsWith('tts-') || modelId.includes('tts'))) {
      serviceType = 'tts';
    }
    
    // Use the enhanced tracking function
    return await updateUserSpendingV2(costInfo, userId, dynamoUtils, categoryName, serviceType);
  } catch (error) {
    console.error('Error updating user spending:', error);
    return false;
  }
};
    

/**
 * Update user spending with separate TTS and Bedrock tracking
 * @param {Object} costInfo - Cost information from calculateTTSCost or calculateTokenCost
 * @param {string} userId - The user ID
 * @param {Object} dynamoUtils - DynamoDB utility instance
 * @param {string} categoryName - Category name for the spending
 * @param {string} serviceType - 'tts' or 'bedrock' for separate tracking
 * @returns {Promise<boolean>} - Whether the update was successful
 */
export const updateUserSpendingV2 = async (costInfo, userId, dynamoUtils, categoryName, serviceType = 'bedrock') => {
  if (!userId || !dynamoUtils || !categoryName) {
    console.error('Cannot update user spending - missing required parameters');
    return false;
  }
  
  try {
    console.log(`Updating ${serviceType} spending for category: ${categoryName}`, costInfo);
    
    const userProfile = await dynamoUtils.getUserProfile(userId);
    if (!userProfile) {
      console.error('User profile not found, creating new one');
      return false;
    }
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const currentWeek = getWeekNumber(new Date());
    
    // Initialize spending structure if it doesn't exist
    if (!userProfile.spending) {
      userProfile.spending = {
        // Combined totals
        total_spend: 0,
        monthly_spend: 0,
        weekly_spend: 0,
        
        // Service-specific totals
        bedrock_total: 0,
        bedrock_monthly: 0,
        bedrock_weekly: 0,
        openai_total: 0,
        openai_monthly: 0,
        openai_weekly: 0,
        tts_total: 0,
        tts_monthly: 0,
        tts_weekly: 0,
        
        last_updated: Date.now(),
        current_month: currentMonth,
        current_year: currentYear,
        current_week: currentWeek,
        category_spend: {},
        usage_history: []
      };
    }
    
    // Initialize missing fields for backward compatibility
    if (userProfile.spending.bedrock_total === undefined) userProfile.spending.bedrock_total = 0;
    if (userProfile.spending.bedrock_monthly === undefined) userProfile.spending.bedrock_monthly = 0;
    if (userProfile.spending.bedrock_weekly === undefined) userProfile.spending.bedrock_weekly = 0;
    if (userProfile.spending.openai_total === undefined) userProfile.spending.openai_total = 0;
    if (userProfile.spending.openai_monthly === undefined) userProfile.spending.openai_monthly = 0;
    if (userProfile.spending.openai_weekly === undefined) userProfile.spending.openai_weekly = 0;
    if (userProfile.spending.tts_total === undefined) userProfile.spending.tts_total = 0;
    if (userProfile.spending.tts_monthly === undefined) userProfile.spending.tts_monthly = 0;
    if (userProfile.spending.tts_weekly === undefined) userProfile.spending.tts_weekly = 0;
    
    // Handle month/week resets
    if (userProfile.spending.current_month !== currentMonth || 
        userProfile.spending.current_year !== currentYear) {
      userProfile.spending.monthly_spend = 0;
      userProfile.spending.bedrock_monthly = 0;
      userProfile.spending.openai_monthly = 0;
      userProfile.spending.tts_monthly = 0;
      userProfile.spending.current_month = currentMonth;
      userProfile.spending.current_year = currentYear;
    }
    
    if (userProfile.spending.current_week !== currentWeek) {
      userProfile.spending.weekly_spend = 0;
      userProfile.spending.bedrock_weekly = 0;
      userProfile.spending.openai_weekly = 0;
      userProfile.spending.tts_weekly = 0;
      userProfile.spending.current_week = currentWeek;
    }
    
    // Update totals
    userProfile.spending.total_spend = (parseFloat(userProfile.spending.total_spend) || 0) + costInfo.totalCost;
    userProfile.spending.monthly_spend = (parseFloat(userProfile.spending.monthly_spend) || 0) + costInfo.totalCost;
    userProfile.spending.weekly_spend = (parseFloat(userProfile.spending.weekly_spend) || 0) + costInfo.totalCost;
    
    // Update service-specific totals for TTS only (to maintain TTS limits functionality)
    // For AI services, we only use category-based tracking now to avoid double-counting
    if (serviceType === 'tts') {
      userProfile.spending.tts_total = (parseFloat(userProfile.spending.tts_total) || 0) + costInfo.totalCost;
      userProfile.spending.tts_monthly = (parseFloat(userProfile.spending.tts_monthly) || 0) + costInfo.totalCost;
      userProfile.spending.tts_weekly = (parseFloat(userProfile.spending.tts_weekly) || 0) + costInfo.totalCost;
    }
    // Note: Legacy bedrock_total and openai_total are no longer updated to prevent double-counting
    // All AI spending is now tracked via category_spend only
    
    // Update category spend
    if (!userProfile.spending.category_spend) {
      userProfile.spending.category_spend = {};
    }
    userProfile.spending.category_spend[categoryName] = 
      (parseFloat(userProfile.spending.category_spend[categoryName]) || 0) + costInfo.totalCost;
    
    // Update timestamp
    userProfile.spending.last_updated = Date.now();
    
    // Add to usage history
    if (!userProfile.spending.usage_history) {
      userProfile.spending.usage_history = [];
    }
    
    const historyEntry = {
      timestamp: Date.now(),
      modelId: costInfo.modelId,
      category: categoryName,
      serviceType: serviceType,
      cost: costInfo.totalCost
    };
    
    // Add service-specific fields
    if (serviceType === 'tts') {
      historyEntry.textLength = costInfo.textLength;
      historyEntry.inputTokens = costInfo.inputTokens;
      historyEntry.audioTokens = costInfo.audioTokens;
    } else {
      historyEntry.inputTokens = costInfo.inputTokens;
      historyEntry.outputTokens = costInfo.outputTokens;
      historyEntry.totalTokens = costInfo.totalTokens;
    }
    
    userProfile.spending.usage_history.unshift(historyEntry);
    
    // Limit history
    if (userProfile.spending.usage_history.length > 1000) {
      userProfile.spending.usage_history = userProfile.spending.usage_history.slice(0, 1000);
    }
    
    return await dynamoUtils.saveUserProfile(userProfile, userId);
  } catch (error) {
    console.error('Error updating user spending:', error);
    return false;
  }
};